import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { getStepsIndication } from '@dealstack/shared/lib/format/string';
import { Button, Flex, Text, useMantineTheme } from '@mantine/core';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

interface Props {
	className?: string;
	title?: string;
	sidebarItemsCount?: number;
	activeMainStepIndex?: number;
	activeSubStepIndex?: number;
	onPrevStep: () => void;
	isPrevDisabled: boolean;
	onNextStep: () => void;
	isNextDisabled: boolean;
	isLastStep: boolean;
	redirectLink: string;
}

const MainHeaderBar = ({
	title,
	activeMainStepIndex,
	activeSubStepIndex,
	sidebarItemsCount,
	onPrevStep,
	isPrevDisabled,
	onNextStep,
	isNextDisabled,
	isLastStep,
	className,
	redirectLink,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const mantineTheme = useMantineTheme();

	return (
		<div className={className}>
			<Link to={redirectLink}>
				<Flex
					gap="0.5rem"
					align="center"
					data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_BACK_TO_DASHBOARD_BUTTON}
				>
					<Icon variant="arrow-left" size="xl" color={mantineTheme.colors.gray[9]} />
					<Text size="lg" c="gray.9">
						{tInvestor('investment.header.back_programme')}
					</Text>
				</Flex>
			</Link>
			<Flex gap="1rem">
				<Text size="lg" c="gray.2" fw={600}>
					{title}
				</Text>
				{!!sidebarItemsCount && (
					<Text
						size="lg"
						c="gray.9"
						data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_WIZARD_STEP_INDICATOR}
					>
						{getStepsIndication(
							activeMainStepIndex,
							activeSubStepIndex,
							sidebarItemsCount
						)}
					</Text>
				)}
			</Flex>
			<Flex gap="1rem">
				<Button
					onClick={onPrevStep}
					disabled={isPrevDisabled}
					variant="secondary-dark"
					size="sm"
					leftSection={<Icon variant="arrow-left" size="lg" />}
					data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_PREVIOUS_STEP_BUTTON}
				>
					{tInvestor('investment.header.previous_step')}
				</Button>

				<Button
					onClick={onNextStep}
					disabled={isNextDisabled}
					variant="primary-dark"
					size="sm"
					rightSection={
						isLastStep ? (
							<Icon variant="tick" size="lg" />
						) : (
							<Icon variant="arrow-right" size="lg" />
						)
					}
					data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_NEXT_STEP_BUTTON}
				>
					{tInvestor(`investment.header.${isLastStep ? 'done' : 'next_step'}`)}
				</Button>
			</Flex>
		</div>
	);
};

const Memoized = memo(MainHeaderBar);
const Styled = styled(Memoized)`
	display: grid;
	align-items: center;
	grid-template-columns: 14rem 1fr min-content;
	grid-gap: 5rem;
	vertical-align: center;
	padding: 1em;
	background-color: ${({ theme }) => theme.v3.colors.dark[8]};
	border-radius: 0.5rem;
	box-shadow: 0 4px 8px 0 #dcdfe0;

	/* The global anchor reset (GlobalStyling) covers :link/:visited/:active but
	   not :hover, so the back-link text shifts colour on hover. Hold it. */
	a:hover .mantine-Text-root {
		color: ${({ theme }) => theme.v3.colors.gray[9]};
	}
`;

export { Styled as MainHeaderBar };
